import type { DataNode } from "antd/es/tree";
import { isNil, map, nth, pipe, prop, sum } from "ramda";
import { Key } from "react";

export interface ITree {
  key: string;
  title: string;
  isLeaf?: boolean;
  children?: ITree[];
  dataCount?: number;
  count?: number;
}
// 根据key返回节点数据
export const findLeafNodesByKeys = (
  keys: Key[],
  nodes: DataNode[]
): DataNode[] => {
  const result = [];

  for (const node of nodes) {
    if (node.isLeaf && keys.includes(node.key)) {
      result.push({
        title: node.title,
        key: node.key,
      });
    }
    if (node.children) {
      const childrenResult = findLeafNodesByKeys(keys, node.children);

      if (childrenResult.length > 0) {
        result.push(...childrenResult);
      }
    }
  }

  return result;
};

// 根据key返回父节点数据
export const findFatherNodesByKeys = (
  keys: Key[],
  nodes: DataNode[]
): Key[] => {
  const result = [];

  for (const node of nodes) {
    if (!node.isLeaf && keys.includes(node.key)) {
      result.push(node.key);
    }
    if (node.children) {
      const childrenResult = findFatherNodesByKeys(keys, node.children);

      if (childrenResult.length > 0) {
        result.push(...childrenResult);
      }
    }
  }

  return result;
};

// 帶有架構邏輯的勾選展示nodes
export const getShowNodesByKeys = (
  keys: Key[],
  nodes: DataNode[]
): DataNode[] => {
  const result = [];

  for (const node of nodes) {
    if (!node.isLeaf && keys.includes(node.key)) {
      result.push(node);
    } else if (node.isLeaf && keys.includes(node.key)) {
      result.push(node);
    } else if (node.children) {
      const childrenResult = getShowNodesByKeys(keys, node.children);

      if (childrenResult.length > 0) {
        result.push(...childrenResult);
      }
    }
  }

  return result;
};

// 根据key返回数据，父子不關聯
export const findNodesByKeys = (keys: Key[], nodes: DataNode[]): DataNode[] => {
  const result = [];

  for (const node of nodes) {
    if (keys.includes(node.key)) {
      result.push({
        title: node.title,
        key: node.key,
      });
    }
    if (node.children && !node.isLeaf) {
      const childrenResult = findNodesByKeys(keys, node.children);

      if (childrenResult) {
        result.push(...childrenResult);
      }
    }
  }

  return result;
};

export const findChildrenNodes = (node: DataNode): DataNode[] => {
  const result: DataNode[] = [];

  const childrenNodes = (node: DataNode) => {
    if (node.children) {
      for (const n of node.children) {
        result.push(n);
        childrenNodes(n);
      }
    }
  };

  childrenNodes(node);

  return result;
};

// 根据key返回children数据
export const findChildrenNodesByKeys = (
  key: Key[],
  nodes: DataNode[]
): DataNode[] => {
  const result = [];

  for (const node of nodes) {
    if (key.includes(node.key) && node.children) {
      const childrenNodes = findChildrenNodes(node);

      result.push(...childrenNodes);

      break;
    } else if (node.children && !node.isLeaf) {
      const childrenResult = findChildrenNodesByKeys(key, node.children);

      if (node.children) {
        if (childrenResult.length > 0) {
          result.push(...childrenResult);
        }
      }
    }
  }

  return result;
};

// 根据key查询父节点
export const getParentKey = (key: React.Key, tree: DataNode[]): React.Key => {
  let parentKey: React.Key;

  for (let i = 0; i < tree.length; i++) {
    const node = nth(i, tree);

    if (node?.children) {
      if (node.children.some((item) => item.key === key)) {
        parentKey = node.key;
      } else if (getParentKey(key, node.children)) {
        parentKey = getParentKey(key, node.children);
      }
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return parentKey!;
};

// 根据key查询父节点
export const getAllParentsKey = (
  key: React.Key,
  tree: DataNode[]
): React.Key[] => {
  const parentKey: React.Key[] = [];

  if (!isNil(getParentKey(key, tree))) {
    const result = getAllParentsKey(getParentKey(key, tree), tree);

    parentKey.push(...result);
  }

  parentKey.push(getParentKey(key, tree));

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return parentKey!;
};

// 树形数据转list
export const generateList = (
  data: DataNode[],
  treeDataList: {
    key: React.Key;
    title: string;
    isLeaf: undefined | boolean;
    parentKey?: React.Key;
  }[],
  parentKey: React.Key = "0"
) => {
  for (let i = 0; i < data.length; i++) {
    const node = nth(i, data);

    if (!isNil(node)) {
      const { key, title, isLeaf } = node;

      treeDataList.push({
        key,
        title: title as string,
        isLeaf: isLeaf,
        parentKey: parentKey,
      });
      if (node.children) {
        generateList(node.children, treeDataList, key);
      }
    }
  }
};

// 根据key更新treeData节点数据
export const updateTreeNodeByKey = (
  key: Key,
  newData: any,
  treeData: any[]
): boolean => {
  for (let i = 0; i < treeData.length; i++) {
    const node = nth(i, treeData);

    if (node.key === key) {
      Object.assign(node, newData);

      return true;
    } else if (node.children) {
      const found = updateTreeNodeByKey(key, newData, node.children);

      if (found) {
        return true;
      }
    }
  }

  return false;
};

// 在指定节点同一层级插入新节点
export const addSiblingNode = (
  treeData: any[],
  key: string,
  newNode: any
): boolean => {
  for (let i = 0; i < treeData.length; i++) {
    const node = nth(i, treeData);

    if (node.key === key) {
      const parent = getParentNode(treeData, node.key);

      if (!parent) {
        // 如果當前節點沒有父節點，則說明它是根節點，直接在根節點後面插入新節點
        treeData.splice(i + 1, 0, newNode);
      } else {
        // 否則在父節點的children數組中找到當前節點，並在它後面插入新節點
        for (let j = 0; j < parent.children!.length; j++) {
          if (nth(j, parent.children!)?.key === node.key) {
            parent.children!.splice(j + 1, 0, newNode);
            break;
          }
        }
      }

      return true;
    } else if (node.children && addSiblingNode(node.children, key, newNode)) {
      return true;
    }
  }

  return false;
};

export const getParentNode = (
  treeData: DataNode[],
  key: string
): DataNode | null => {
  for (let i = 0; i < treeData.length; i++) {
    const node = nth(i, treeData);

    if (!isNil(node)) {
      if (node.key === key) {
        return node;
      } else if (node.children) {
        const parent = getParentNode(node.children, key);

        if (parent) {
          return parent;
        }
      }
    }
  }

  return null;
};

// 指定key下新增一个子节点
export const addChildNodeByKey = (
  treeData: any[],
  key: string,
  newNode: any
) => {
  for (let i = 0; i < treeData.length; i++) {
    const node = nth(i, treeData);

    if (node.key === key) {
      if (!node.children) {
        node.children = [];
      }
      node.children.push(newNode);
      break;
    } else if (node.children) {
      addChildNodeByKey(node.children, key, newNode);
    }
  }
};

// 根据key删除节点
export const deleteNodeByKey = (data: any[], key: Key): any[] => {
  return data.filter((item) => {
    if (item.key === key) {
      return false;
    } else if (item.children) {
      item.children = deleteNodeByKey(item.children, key);

      return true;
    } else {
      return true;
    }
  });
};

export const getTitleByKey = (
  key: string,
  tree: ITree[]
): string | undefined => {
  for (const node of tree) {
    if (node.key === key) {
      return node.title;
    }
    if (node.children) {
      const title = getTitleByKey(key, node.children);

      if (title) {
        return title;
      }
    }
  }

  return undefined;
};

export const getisLeafByKey = (
  key: string,
  tree: ITree[] | DataNode[]
): boolean | undefined => {
  for (const node of tree) {
    if (node.key === key) {
      return node.isLeaf;
    }
    if (node.children) {
      const isLeaf = getisLeafByKey(key, node.children);

      if (typeof isLeaf === "boolean") {
        return isLeaf;
      }
    }
  }

  return undefined;
};
